import {
  Add,
  Apartment,
  Gavel,
  LocationCity,
  DeleteOutline,
  OpenInNew,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  useAdminUnitContext,
  useCreateCourtAdminUnitAssignment,
  useDeleteCourtAdminUnitAssignment,
  useCreateAdminUnitMunicipality,
  useDeleteAdminUnitMunicipality,
  useAdminUnitPostcodes,
  useCreateAdminUnitPostcode,
  useDeleteAdminUnitPostcode,
} from '../../Aemter/hooks';
import { PostcodeSearch } from '../../Clients/AddressTab/PostcodeSearch';
import { ApiError } from '../../hooks/useFetch';
import { useAlert } from '../../context/AlertContext';
import type { IPostcode } from '../../Interfaces/IPostcode';
import type { ICourt } from '../../Interfaces/ICourt';
import type { ICourtRole } from '../../Interfaces/ICourtRole';
import type { ICourtAdminstrativeUnitAssignment } from '../../Interfaces/ICourtAdminstrativeUnitAssignment';
import type { IAdminstrativeUnit } from '../../Interfaces/IAdminstrativeUnit';
import type { IFederalState } from '../../Interfaces/IAemter';
import type {
  IMunicipality,
  IAdminUnitMunicipality,
} from '../../Interfaces/IMunicipality';
import type { AemterNavigate } from './aemterNav';

interface IProps {
  adminUnit: IAdminstrativeUnit;
  federalStates: IFederalState[];
  courts: ICourt[];
  courtRoles: ICourtRole[];
  assignments: ICourtAdminstrativeUnitAssignment[];
  /** Alle Gemeinden (für die Zuordnung „Gemeinde ändern"). */
  municipalities: IMunicipality[];
  /** Gemarkung↔Gemeinde-Mappings (die editierbare Verknüpfung). */
  adminUnitMunicipalities: IAdminUnitMunicipality[];
  onAnyChange: () => void;
  onNavigate?: AemterNavigate;
}

const extractErrorText = (e: unknown, fallback: string): string => {
  if (
    e instanceof ApiError &&
    e.body &&
    typeof e.body === 'object' &&
    'fehler' in e.body
  ) {
    const f = (e.body as { fehler?: unknown }).fehler;
    if (typeof f === 'string' && f) return f;
  }
  return fallback;
};

/**
 * Detail-Pane für eine Gemarkung. Fachlich hängt an der Gemarkung genau EINE
 * Stelle: das zuständige Grundbuchamt. Deshalb gibt es hier bewusst KEINE
 * Rollenauswahl — die Rolle ist immer „Grundbuchamt". Zusätzlich werden die
 * zugeordnete(n) politische(n) Gemeinde(n) und die PLZ angezeigt.
 */
export const GemarkungDetailPane: React.FC<IProps> = ({
  adminUnit,
  federalStates,
  courts,
  courtRoles,
  assignments,
  municipalities,
  adminUnitMunicipalities,
  onAnyChange,
  onNavigate,
}) => {
  const { showAlert } = useAlert();
  const { createCourtAdminUnitAssignment, isPendingCreateAdminUnitAssignment } =
    useCreateCourtAdminUnitAssignment();
  const { deleteCourtAdminstrativeUnitAssignment, isPending: isPendingDelete } =
    useDeleteCourtAdminUnitAssignment();
  const { createAdminUnitMunicipality } = useCreateAdminUnitMunicipality();
  const { deleteAdminUnitMunicipality } = useDeleteAdminUnitMunicipality();
  const {
    adminUnitContext,
    isLoadingAdminUnitContext,
    refetchAdminUnitContext,
  } = useAdminUnitContext(adminUnit.idAdministrativeUnit);
  const {
    adminUnitPostcodes,
    isLoadingAdminUnitPostcodes,
    refetchAdminUnitPostcodes,
  } = useAdminUnitPostcodes(adminUnit.idAdministrativeUnit);
  const { createAdminUnitPostcode, isPendingCreateAdminUnitPostcode } =
    useCreateAdminUnitPostcode();
  const { deleteAdminUnitPostcode } = useDeleteAdminUnitPostcode();

  const [courtToAssign, setCourtToAssign] = useState<ICourt | null>(null);
  const [municipalityToAdd, setMunicipalityToAdd] =
    useState<IMunicipality | null>(null);
  const [postcodeToAdd, setPostcodeToAdd] = useState<IPostcode | null>(null);
  // Suchfelder erst nach Klick auf den „+"-Chip einblenden.
  const [addingMunicipality, setAddingMunicipality] = useState(false);
  const [addingPostcode, setAddingPostcode] = useState(false);

  const fsName =
    federalStates.find(f => f.idFederalState === adminUnit.idFederalState)
      ?.FederalState ?? '';

  const grundbuchamtRole = courtRoles.find(r => r.CourtRole === 'Grundbuchamt');

  // Aktuelle Grundbuchamt-Zuordnung dieser Gemarkung (genau eine).
  const current = assignments.find(
    a =>
      a.idAdministrativeUnit === adminUnit.idAdministrativeUnit &&
      a.activeAssignment !== false &&
      !!a.idCourt &&
      (grundbuchamtRole ? a.idCourtRole === grundbuchamtRole.idCourtRole : true)
  );
  const currentCourt = current
    ? courts.find(c => c.idCourt === current.idCourt)
    : undefined;

  // Wählbare Grundbuchämter: gleiches Bundesland, aktiv.
  const selectableCourts = courts
    .filter(
      c =>
        c.idFederalState === adminUnit.idFederalState && c.activeCourt !== false
    )
    .sort((a, b) => a.Court.localeCompare(b.Court));

  const handleAssign = async () => {
    if (!courtToAssign || !grundbuchamtRole) return;
    try {
      await createCourtAdminUnitAssignment({
        idAssignment: -1,
        idCourt: courtToAssign.idCourt,
        idAuthority: null,
        idAdministrativeUnit: adminUnit.idAdministrativeUnit,
        idCourtRole: grundbuchamtRole.idCourtRole,
        idFederalState: adminUnit.idFederalState,
        activeAssignment: true,
      });
      showAlert({ text: 'Grundbuchamt zugeordnet' });
      setCourtToAssign(null);
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({
        severity: 'error',
        text: extractErrorText(e, 'Fehler beim Speichern'),
      });
    }
  };

  const handleRemove = async () => {
    if (!current) return;
    try {
      await deleteCourtAdminstrativeUnitAssignment(current);
      showAlert({ text: 'Zuordnung entfernt' });
      onAnyChange();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  // --- Gemeinde-Zuordnung (Gemarkung↔Gemeinde) editierbar ---
  const myMunicipalityMappings = adminUnitMunicipalities.filter(
    m =>
      m.idAdministrativeUnit === adminUnit.idAdministrativeUnit &&
      m.activeAssignment !== false
  );
  const mappedMunicipalityIds = new Set(
    myMunicipalityMappings.map(m => m.idMunicipality)
  );
  const municipalityName = (id: number) =>
    municipalities.find(m => m.idMunicipality === id)?.Municipality ??
    `Gemeinde ${id}`;

  // Zuordenbare Gemeinden: gleiches Bundesland, noch nicht verknüpft.
  const addableMunicipalities = municipalities
    .filter(
      m =>
        m.idFederalState === adminUnit.idFederalState &&
        m.activeMunicipality !== false &&
        !mappedMunicipalityIds.has(m.idMunicipality)
    )
    .sort((a, b) => a.Municipality.localeCompare(b.Municipality));

  const handleAddMunicipality = async () => {
    if (!municipalityToAdd) return;
    try {
      await createAdminUnitMunicipality({
        idAdminUnitMunicipality: -1,
        idAdministrativeUnit: adminUnit.idAdministrativeUnit,
        idMunicipality: municipalityToAdd.idMunicipality,
        activeAssignment: true,
      });
      showAlert({ text: 'Gemeinde zugeordnet' });
      setMunicipalityToAdd(null);
      setAddingMunicipality(false);
      onAnyChange();
      refetchAdminUnitContext();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  const handleRemoveMunicipality = async (m: IAdminUnitMunicipality) => {
    try {
      await deleteAdminUnitMunicipality(m.idAdminUnitMunicipality);
      showAlert({ text: 'Gemeinde-Zuordnung entfernt' });
      onAnyChange();
      refetchAdminUnitContext();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  // --- Direkte PLZ-Zuordnung (Gemarkung↔PLZ) ---
  const directPostcodes = adminUnitPostcodes ?? [];
  const directPostcodeIds = new Set(directPostcodes.map(p => p.idPostcode));
  // Vorschläge: PLZ der zugeordneten Gemeinde(n), die noch nicht direkt hängen.
  const suggestablePostcodes = (adminUnitContext?.postcodes ?? []).filter(
    p => !directPostcodeIds.has(p.idPostcode)
  );

  const addDirectPostcode = async (idPostcode: number) => {
    try {
      await createAdminUnitPostcode({
        idAdminUnitPostcode: -1,
        idAdministrativeUnit: adminUnit.idAdministrativeUnit,
        idPostcode,
        activeAssignment: true,
      });
      showAlert({ text: 'PLZ zugeordnet' });
      setPostcodeToAdd(null);
      setAddingPostcode(false);
      void refetchAdminUnitPostcodes();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Speichern' });
    }
  };

  const removeDirectPostcode = async (idAdminUnitPostcode: number) => {
    try {
      await deleteAdminUnitPostcode(idAdminUnitPostcode);
      showAlert({ text: 'PLZ-Zuordnung entfernt' });
      void refetchAdminUnitPostcodes();
    } catch (e) {
      console.error(e);
      showAlert({ severity: 'error', text: 'Fehler beim Entfernen' });
    }
  };

  return (
    <Box>
      {/* Stammdaten */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Apartment color='primary' />
            <Box>
              <Typography variant='h6'>
                {adminUnit.AdministrativeUnit}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {fsName}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Grundbuchamt */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
            Zuständiges Grundbuchamt
          </Typography>
          {current && currentCourt ? (
            <Box
              sx={{
                p: 1.25,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Gavel fontSize='small' />
              <Typography variant='body2' fontWeight={600} sx={{ flex: 1 }}>
                {currentCourt.Court}
              </Typography>
              {onNavigate && (
                <Tooltip title='Zum Gericht springen'>
                  <IconButton
                    size='small'
                    onClick={() =>
                      onNavigate({ kind: 'court', id: currentCourt.idCourt })
                    }
                  >
                    <OpenInNew fontSize='inherit' />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title='Zuordnung entfernen'>
                <span>
                  <Button
                    size='small'
                    color='error'
                    startIcon={<DeleteOutline />}
                    onClick={() => void handleRemove()}
                    disabled={isPendingDelete}
                  >
                    Entfernen
                  </Button>
                </span>
              </Tooltip>
            </Box>
          ) : (
            <Stack direction='row' spacing={1} alignItems='center'>
              <Autocomplete
                sx={{ flex: 1 }}
                size='small'
                options={selectableCourts}
                value={courtToAssign}
                onChange={(_, v) => setCourtToAssign(v)}
                getOptionLabel={o => o.Court}
                isOptionEqualToValue={(o, v) => o.idCourt === v.idCourt}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Grundbuchamt wählen'
                    placeholder='Gericht suchen…'
                  />
                )}
              />
              <Button
                variant='contained'
                onClick={() => void handleAssign()}
                disabled={
                  !courtToAssign ||
                  !grundbuchamtRole ||
                  isPendingCreateAdminUnitAssignment
                }
              >
                {isPendingCreateAdminUnitAssignment ? (
                  <CircularProgress size={18} />
                ) : (
                  'Zuordnen'
                )}
              </Button>
            </Stack>
          )}
          {!grundbuchamtRole && (
            <Alert severity='warning' sx={{ mt: 1 }}>
              Die Rolle „Grundbuchamt" fehlt im Rollenkatalog.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Zugeordnete Gemeinde(n) + PLZ */}
      <Card variant='outlined'>
        <CardContent>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
            Zugeordnete Gemeinde &amp; Postleitzahlen
          </Typography>
          {isLoadingAdminUnitContext ? (
            <CircularProgress size={22} />
          ) : (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Politische Gemeinde(n)
                </Typography>
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
                >
                  {myMunicipalityMappings.map(m => (
                    <Chip
                      key={m.idAdminUnitMunicipality}
                      icon={<LocationCity />}
                      size='small'
                      variant='outlined'
                      clickable={!!onNavigate}
                      onClick={
                        onNavigate
                          ? () =>
                              onNavigate({
                                kind: 'municipality',
                                id: m.idMunicipality,
                              })
                          : undefined
                      }
                      onDelete={() => void handleRemoveMunicipality(m)}
                      deleteIcon={
                        <Tooltip title='Gemeinde-Zuordnung entfernen'>
                          <DeleteOutline />
                        </Tooltip>
                      }
                      label={municipalityName(m.idMunicipality)}
                    />
                  ))}
                  {!addingMunicipality && (
                    <Chip
                      icon={<Add />}
                      size='small'
                      color='primary'
                      variant='outlined'
                      clickable
                      onClick={() => setAddingMunicipality(true)}
                      label='Gemeinde hinzufügen'
                    />
                  )}
                </Box>
                {/* Suche erst nach Klick auf den „+"-Chip */}
                {addingMunicipality && (
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{ mt: 1 }}
                  >
                    <Autocomplete
                      sx={{ flex: 1 }}
                      size='small'
                      openOnFocus
                      options={addableMunicipalities}
                      value={municipalityToAdd}
                      onChange={(_, v) => setMunicipalityToAdd(v)}
                      getOptionLabel={o =>
                        o.AGS
                          ? `${o.Municipality} (AGS ${o.AGS})`
                          : o.Municipality
                      }
                      isOptionEqualToValue={(o, v) =>
                        o.idMunicipality === v.idMunicipality
                      }
                      filterOptions={(opts, state) => {
                        const q = state.inputValue.trim().toLowerCase();
                        if (!q) return opts.slice(0, 50);
                        return opts
                          .filter(o => o.Municipality.toLowerCase().includes(q))
                          .slice(0, 50);
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          autoFocus
                          label='Gemeinde zuordnen'
                          placeholder='Gemeinde suchen…'
                        />
                      )}
                    />
                    <Button
                      variant='contained'
                      onClick={() => void handleAddMunicipality()}
                      disabled={!municipalityToAdd}
                    >
                      Zuordnen
                    </Button>
                    <Button
                      onClick={() => {
                        setAddingMunicipality(false);
                        setMunicipalityToAdd(null);
                      }}
                    >
                      Abbrechen
                    </Button>
                  </Stack>
                )}
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Direkt zugeordnete Postleitzahlen ({directPostcodes.length})
                </Typography>
                {isLoadingAdminUnitPostcodes ? (
                  <CircularProgress
                    size={18}
                    sx={{ display: 'block', mt: 0.5 }}
                  />
                ) : (
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {directPostcodes.map(p => (
                        <Chip
                          key={p.idAdminUnitPostcode}
                          size='small'
                          variant='outlined'
                          color='primary'
                          clickable={!!onNavigate}
                          onClick={
                            onNavigate
                              ? () =>
                                  onNavigate({
                                    kind: 'postcode',
                                    postcode: {
                                      idPostcode: p.idPostcode,
                                      Postcode: p.Postcode ?? '',
                                      City: p.City ?? '',
                                      idCountry: null,
                                    },
                                  })
                              : undefined
                          }
                          onDelete={() =>
                            void removeDirectPostcode(p.idAdminUnitPostcode)
                          }
                          deleteIcon={
                            <Tooltip title='PLZ-Zuordnung entfernen'>
                              <DeleteOutline />
                            </Tooltip>
                          }
                          label={`${p.Postcode ?? ''} ${p.City ?? ''}`}
                        />
                      ))}
                      {!addingPostcode && (
                        <Chip
                          icon={<Add />}
                          size='small'
                          color='primary'
                          variant='outlined'
                          clickable
                          onClick={() => setAddingPostcode(true)}
                          label='PLZ hinzufügen'
                        />
                      )}
                    </Box>
                    {directPostcodes.length === 0 && !addingPostcode && (
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        sx={{ mt: 0.5 }}
                      >
                        Noch keine PLZ direkt zugeordnet.
                      </Typography>
                    )}
                    {addingPostcode && (
                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ mt: 1 }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <PostcodeSearch
                            postcodeObject={postcodeToAdd}
                            updatePostcodeObject={p =>
                              setPostcodeToAdd(p ?? null)
                            }
                            required={false}
                          />
                        </Box>
                        <Button
                          variant='contained'
                          onClick={() =>
                            postcodeToAdd?.idPostcode &&
                            void addDirectPostcode(postcodeToAdd.idPostcode)
                          }
                          disabled={
                            !postcodeToAdd?.idPostcode ||
                            isPendingCreateAdminUnitPostcode
                          }
                        >
                          Zuordnen
                        </Button>
                        <Button
                          onClick={() => {
                            setAddingPostcode(false);
                            setPostcodeToAdd(null);
                          }}
                        >
                          Abbrechen
                        </Button>
                      </Stack>
                    )}
                  </>
                )}

                {/* Schnell-Zuordnung: PLZ der Gemeinde, noch nicht direkt gesetzt */}
                {suggestablePostcodes.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Weitere PLZ der Gemeinde (zum Zuordnen anklicken)
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      {suggestablePostcodes.map(p => (
                        <Chip
                          key={p.idPostcode}
                          size='small'
                          variant='outlined'
                          icon={<Add />}
                          clickable
                          onClick={() => void addDirectPostcode(p.idPostcode)}
                          label={`${p.Postcode} ${p.City}`}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
